import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import PersonnelToast from '../../components/PersonnelToast';
import Panel from 'src/components/common/Panel';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import useFetch from 'use-http';
import EmployeeDashboardFilteredObject from 'src/models/personnel/EmployeeDashboardFilteredObject';
import EmployeeBenefitDashboard from 'src/models/personnel/EmployeeBenefitDashboard';
import EmployeeBenefitFilteredDashboard from 'src/models/personnel/EmployeeBenefitFilteredDashboard';
import { FaSortAlphaDown, FaSortAlphaUp, FaFileExcel } from 'react-icons/fa';

// (label, model field, column width) - drives both the header cells and their sort buttons,
// matching the sortable-column pattern in DepositReconciliationPage.tsx's Outstanding/Reconciled
// panels.
const BENEFIT_COLUMNS: Array<{ label: string; field: keyof EmployeeBenefitFilteredDashboard; width: string }> = [
    { label: 'No.', field: 'tsdNo', width: '5%' },
    { label: 'Name', field: 'akaName', width: '18%' },
    { label: 'Health', field: 'health', width: '5%' },
    { label: 'Vision', field: 'vision', width: '4%' },
    { label: 'Dental', field: 'dental', width: '4%' },
    { label: 'Life', field: 'life', width: '4%' },
    { label: 'Disability', field: 'disability', width: '4%' },
    { label: 'Total Ins', field: 'ins', width: '4%' },
    { label: 'Vacation', field: 'vacation', width: '4%' },
    { label: 'Personal', field: 'personal', width: '4%' },
    { label: 'Holiday', field: 'holiday', width: '4%' },
    { label: '401K', field: '_401k', width: '4%' },
    { label: 'FICA', field: 'fica', width: '4%' },
    { label: 'Work Comp', field: 'wc', width: '4%' },
    { label: 'Ben. Total', field: 'ben', width: '4%' },
    { label: 'w/o PTO', field: 'exPto', width: '4%' },
    { label: 'Salary', field: 'salary', width: '4%' },
    { label: 'Ben + Salary', field: 'payBen', width: '4%' },
];

interface IBenefitDashboardDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
}

const BenefitDashboardDialog = (props: IBenefitDashboardDialogProps) => {
    const dispatch = useDispatch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [baseDashboardInformation, setBaseDashboardInformation] = useState<Array<EmployeeBenefitDashboard>>();
    const [filteredDashboardInformation, setFilteredDashboardInformation] = useState<Array<EmployeeBenefitFilteredDashboard>>();
    const { register, setValue, getValues, reset } = useForm();
    const [benefitYears, setBenefitYears] = useState<Array<number>>();
    const { get, put, response } = useFetch();
    const [filterOptions, setFilterOptions] = useState<EmployeeDashboardFilteredObject>({ timeSpan: 'Annual', year: undefined, activeFilter: 'Active', category: 'Total' });
    const [sortField, setSortField] = useState<keyof EmployeeBenefitFilteredDashboard | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        getFilteredResults();
    }, [baseDashboardInformation]);

    useEffect(() => {
        if (filterOptions.year !== undefined) {
            getDashboardInformation();
        }
    }, [filterOptions]);

    const onCreate = () => {
        initializeData();
    };

    const initializeData = async () => {
        setValue('filterBy', 'Annual');
        setValue('view', 'Active');
        await getBenefitYears();
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // benefit years and dropdown list mapping
    const getBenefitYears = async () => {
        let results = await get(`/v1.0/landmen/benefits/get-benefit-years`);

        if (response.ok && results) {
            setBenefitYears(results);
            reset({
                year: results[0],
                filterBy: 'Annual',
                view: 'Active',
                category: 'Total',
            });
            setFilterOptions(prev => ({ ...prev, year: results[0] }));
        } else {
            showToast('Unable to fetch benefit years', `rgba(255,0,0,0.5)`);
        }
    };

    const benefitYearsDropdown = () => {
        return benefitYears
            ? benefitYears?.map((ct: number) => {
                return { value: ct, label: ct.toString() };
            })
            : [];
    };

    const getDashboardInformation = async () => {
        let results = await put(`v1.0/landmen/benefits/employee-benefit-dashboard`, filterOptions);

        if (response.ok) {
            setBaseDashboardInformation(results);
        } else {
            showToast('Unable to fetch benefit dashboard information: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    // filter
    const getFilteredResults = () => {
        let filteredResults = new Array<EmployeeBenefitFilteredDashboard>();
        let filter = filterOptions.timeSpan;
        let category = filterOptions.category;

        switch (filter) {

            case 'Annual':
                baseDashboardInformation?.forEach((entry) => {
                    let newRow = new EmployeeBenefitFilteredDashboard();

                    newRow.tsdNo = entry?.tsdNo;
                    newRow.akaName = entry?.akaName;
                    newRow.year = entry?.year;
                    newRow.defined = entry?.defined;
                    newRow.active = entry?.active;

                    newRow.health = category === 'Total'
                        ? entry?.annualHealth
                        : category === 'Employee'
                            ? entry?.annualEmployeeHealth
                            : entry?.annualCompanyHealth;

                    newRow.dental = category === 'Total'
                        ? entry?.annualDental
                        : category === 'Employee'
                            ? entry?.annualEmployeeDental
                            : entry?.annualCompanyDental;

                    newRow.vision = category === 'Total'
                        ? entry?.annualVision
                        : category === 'Employee'
                            ? entry?.annualEmployeeVision
                            : entry?.annualCompanyVision;

                    newRow.life = category === 'Total'
                        ? entry?.annualLife
                        : category === 'Employee'
                            ? entry?.annualEmployeeLife
                            : entry?.annualCompanyLife;

                    newRow.disability = category === 'Total'
                        ? entry?.annualDisability
                        : category === 'Employee'
                            ? entry?.annualEmployeeDisability
                            : entry?.annualCompanyDisability;

                    newRow.vacation = category === 'Total'
                        ? entry?.annualVacation
                        : category === 'Employee'
                            ? entry?.annualEmployeeVacation
                            : entry?.annualCompanyVacation;

                    newRow.personal = category === 'Total'
                        ? entry?.annualPersonal
                        : category === 'Employee'
                            ? entry?.annualEmployeePersonal
                            : entry?.annualCompanyPersonal;

                    newRow.holiday = category === 'Total'
                        ? entry?.annualHoliday
                        : category === 'Employee'
                            ? entry?.annualEmployeeHoliday
                            : entry?.annualCompanyHoliday;

                    newRow._401k = category === 'Total'
                        ? entry?.annual401k
                        : category === 'Employee'
                            ? entry?.annualEmployee401k
                            : entry?.annualCompany401k;

                    newRow.fica = category === 'Total'
                        ? entry?.annualFica
                        : category === 'Employee'
                            ? entry?.annualEmployeeFica
                            : entry?.annualCompanyFica;

                    newRow.wc = category === 'Total'
                        ? entry?.annualWc
                        : category === 'Employee'
                            ? entry?.annualEmployeeWc
                            : entry?.annualCompanyWc;

                    newRow.ins = category === 'Total'
                        ? entry?.annualIns
                        : category === 'Employee'
                            ? entry?.annualEmployeeIns
                            : entry?.annualCompanyIns;

                    newRow.pto = category === 'Total'
                        ? entry?.annualPto
                        : category === 'Employee'
                            ? entry?.annualEmployeePto
                            : entry?.annualCompanyPto;

                    newRow.exPto = category === 'Total'
                        ? entry?.annualExPto
                        : category === 'Employee'
                            ? entry?.annualEmployeeExPto
                            : entry?.annualCompanyExPto;

                    newRow.ben = category === 'Total'
                        ? entry?.annualBen
                        : category === 'Employee'
                            ? entry?.annualEmployeeBen
                            : entry?.annualCompanyBen;

                    newRow.payBen = category === 'Total'
                        ? entry?.annualPayBen
                        : category === 'Employee'
                            ? entry?.annualEmployeePayBen
                            : entry?.annualCompanyPayBen;

                    newRow.salary = entry?.annualSalary;

                    filteredResults.push(newRow);
                });
                break;
            case 'Month':
                baseDashboardInformation?.forEach((entry) => {
                    let newRow = new EmployeeBenefitFilteredDashboard();

                    newRow.tsdNo = entry?.tsdNo;
                    newRow.akaName = entry?.akaName;
                    newRow.year = entry?.year;
                    newRow.defined = entry?.defined;
                    newRow.active = entry?.active;

                    newRow.health = category === 'Total'
                        ? entry?.monHealth
                        : category === 'Employee'
                            ? entry?.monEmployeeHealth
                            : entry?.monCompanyHealth;

                    newRow.dental = category === 'Total'
                        ? entry?.monDental
                        : category === 'Employee'
                            ? entry?.monEmployeeDental
                            : entry?.monCompanyDental;

                    newRow.vision = category === 'Total'
                        ? entry?.monVision
                        : category === 'Employee'
                            ? entry?.monEmployeeVision
                            : entry?.monCompanyVision;

                    newRow.life = category === 'Total'
                        ? entry?.monLife
                        : category === 'Employee'
                            ? entry?.monEmployeeLife
                            : entry?.monCompanyLife;

                    newRow.disability = category === 'Total'
                        ? entry?.monDisability
                        : category === 'Employee'
                            ? entry?.monEmployeeDisability
                            : entry?.monCompanyDisability;

                    newRow.vacation = category === 'Total'
                        ? entry?.monthVacation
                        : category === 'Employee'
                            ? entry?.monthEmployeeVacation
                            : entry?.monthCompanyVacation;

                    newRow.personal = category === 'Total'
                        ? entry?.monthPersonal
                        : category === 'Employee'
                            ? entry?.monthEmployeePersonal
                            : entry?.monthCompanyPersonal;

                    newRow.holiday = category === 'Total'
                        ? entry?.monthHoliday
                        : category === 'Employee'
                            ? entry?.monthEmployeeHoliday
                            : entry?.monthCompanyHoliday;

                    newRow._401k = category === 'Total'
                        ? entry?.month401k
                        : category === 'Employee'
                            ? entry?.monthEmployee401k
                            : entry?.monthCompany401k;

                    newRow.fica = category === 'Total'
                        ? entry?.monthFica
                        : category === 'Employee'
                            ? entry?.monthEmployeeFica
                            : entry?.monthCompanyFica;

                    newRow.wc = category === 'Total'
                        ? entry?.monthWc
                        : category === 'Employee'
                            ? entry?.monthEmployeeWc
                            : entry?.monthCompanyWc;

                    newRow.ins = category === 'Total'
                        ? entry?.monthIns
                        : category === 'Employee'
                            ? entry?.monthEmployeeIns
                            : entry?.monthCompanyIns;

                    newRow.pto = category === 'Total'
                        ? entry?.monthPto
                        : category === 'Employee'
                            ? entry?.monthEmployeePto
                            : entry?.monthCompanyPto;

                    newRow.exPto = category === 'Total'
                        ? entry?.monthExPto
                        : category === 'Employee'
                            ? entry?.monthEmployeeExPto
                            : entry?.monthCompanyExPto;

                    newRow.ben = category === 'Total'
                        ? entry?.monthBen
                        : category === 'Employee'
                            ? entry?.monthEmployeeBen
                            : entry?.monthCompanyBen;

                    newRow.payBen = category === 'Total'
                        ? entry?.monthPayBen
                        : category === 'Employee'
                            ? entry?.monthEmployeePayBen
                            : entry?.monthCompanyPayBen;

                    newRow.salary = entry?.monthSalary;

                    filteredResults.push(newRow);
                });
                break;
            case 'Period':
                baseDashboardInformation?.forEach((entry) => {
                    let newRow = new EmployeeBenefitFilteredDashboard();

                    newRow.tsdNo = entry?.tsdNo;
                    newRow.akaName = entry?.akaName;
                    newRow.year = entry?.year;
                    newRow.defined = entry?.defined;
                    newRow.active = entry?.active;

                    newRow.health = category === 'Total'
                        ? entry?.periodHealth
                        : category === 'Employee'
                            ? entry?.periodEmployeeHealth
                            : entry?.periodCompanyHealth;

                    newRow.dental = category === 'Total'
                        ? entry?.periodDental
                        : category === 'Employee'
                            ? entry?.periodEmployeeDental
                            : entry?.periodCompanyDental;

                    newRow.vision = category === 'Total'
                        ? entry?.periodVision
                        : category === 'Employee'
                            ? entry?.periodEmployeeVision
                            : entry?.periodCompanyVision;

                    newRow.life = category === 'Total'
                        ? entry?.periodLife
                        : category === 'Employee'
                            ? entry?.periodEmployeeLife
                            : entry?.periodCompanyLife;

                    newRow.disability = category === 'Total'
                        ? entry?.periodDisability
                        : category === 'Employee'
                            ? entry?.periodEmployeeDisability
                            : entry?.periodCompanyDisability;

                    newRow.vacation = category === 'Total'
                        ? entry?.periodVacation
                        : category === 'Employee'
                            ? entry?.periodEmployeeVacation
                            : entry?.periodCompanyVacation;

                    newRow.personal = category === 'Total'
                        ? entry?.periodPersonal
                        : category === 'Employee'
                            ? entry?.periodEmployeePersonal
                            : entry?.periodCompanyPersonal;

                    newRow.holiday = category === 'Total'
                        ? entry?.periodHoliday
                        : category === 'Employee'
                            ? entry?.periodEmployeeHoliday
                            : entry?.periodCompanyHoliday;

                    newRow._401k = category === 'Total'
                        ? entry?.period401k
                        : category === 'Employee'
                            ? entry?.periodEmployee401k
                            : entry?.periodCompany401k;

                    newRow.fica = category === 'Total'
                        ? entry?.periodFica
                        : category === 'Employee'
                            ? entry?.periodEmployeeFica
                            : entry?.periodCompanyFica;

                    newRow.wc = category === 'Total'
                        ? entry?.periodWc
                        : category === 'Employee'
                            ? entry?.periodEmployeeWc
                            : entry?.periodCompanyWc;

                    newRow.ins = category === 'Total'
                        ? entry?.periodIns
                        : category === 'Employee'
                            ? entry?.periodEmployeeIns
                            : entry?.periodCompanyIns;

                    newRow.pto = category === 'Total'
                        ? entry?.periodPto
                        : category === 'Employee'
                            ? entry?.periodEmployeePto
                            : entry?.periodCompanyPto;

                    newRow.exPto = category === 'Total'
                        ? entry?.periodExPto
                        : category === 'Employee'
                            ? entry?.periodEmployeeExPto
                            : entry?.periodCompanyExPto;

                    newRow.ben = category === 'Total'
                        ? entry?.periodBen
                        : category === 'Employee'
                            ? entry?.periodEmployeeBen
                            : entry?.periodCompanyBen;

                    newRow.payBen = category === 'Total'
                        ? entry?.periodPayBen
                        : category === 'Employee'
                            ? entry?.periodEmployeePayBen
                            : entry?.periodCompanyPayBen;

                    newRow.salary = entry?.periodSalary;

                    filteredResults.push(newRow);
                });
                break;
            case 'Day':
                baseDashboardInformation?.forEach((entry) => {
                    let newRow = new EmployeeBenefitFilteredDashboard();

                    newRow.tsdNo = entry?.tsdNo;
                    newRow.akaName = entry?.akaName;
                    newRow.year = entry?.year;
                    newRow.defined = entry?.defined;
                    newRow.active = entry?.active;

                    newRow.health = category === 'Total'
                        ? entry?.dayHealth
                        : category === 'Employee'
                            ? entry?.dayEmployeeHealth
                            : entry?.dayCompanyHealth;

                    newRow.dental = category === 'Total'
                        ? entry?.dayDental
                        : category === 'Employee'
                            ? entry?.dayEmployeeDental
                            : entry?.dayCompanyDental;

                    newRow.vision = category === 'Total'
                        ? entry?.dayVision
                        : category === 'Employee'
                            ? entry?.dayEmployeeVision
                            : entry?.dayCompanyVision;

                    newRow.life = category === 'Total'
                        ? entry?.dayLife
                        : category === 'Employee'
                            ? entry?.dayEmployeeLife
                            : entry?.dayCompanyLife;

                    newRow.disability = category === 'Total'
                        ? entry?.dayDisability
                        : category === 'Employee'
                            ? entry?.dayEmployeeDisability
                            : entry?.dayCompanyDisability;

                    newRow.vacation = category === 'Total'
                        ? entry?.dayVacation
                        : category === 'Employee'
                            ? entry?.dayEmployeeVacation
                            : entry?.dayCompanyVacation;

                    newRow.personal = category === 'Total'
                        ? entry?.dayPersonal
                        : category === 'Employee'
                            ? entry?.dayEmployeePersonal
                            : entry?.dayCompanyPersonal;

                    newRow.holiday = category === 'Total'
                        ? entry?.dayHoliday
                        : category === 'Employee'
                            ? entry?.dayEmployeeHoliday
                            : entry?.dayCompanyHoliday;

                    newRow._401k = category === 'Total'
                        ? entry?.day401k
                        : category === 'Employee'
                            ? entry?.dayEmployee401k
                            : entry?.dayCompany401k;

                    newRow.fica = category === 'Total'
                        ? entry?.dayFica
                        : category === 'Employee'
                            ? entry?.dayEmployeeFica
                            : entry?.dayCompanyFica;

                    newRow.wc = category === 'Total'
                        ? entry?.dayWc
                        : category === 'Employee'
                            ? entry?.dayEmployeeWc
                            : entry?.dayCompanyWc;

                    newRow.ins = category === 'Total'
                        ? entry?.dayIns
                        : category === 'Employee'
                            ? entry?.dayEmployeeIns
                            : entry?.dayCompanyIns;

                    newRow.pto = category === 'Total'
                        ? entry?.dayPto
                        : category === 'Employee'
                            ? entry?.dayEmployeePto
                            : entry?.dayCompanyPto;

                    newRow.exPto = category === 'Total'
                        ? entry?.dayExPto
                        : category === 'Employee'
                            ? entry?.dayEmployeeExPto
                            : entry?.dayCompanyExPto;

                    newRow.ben = category === 'Total'
                        ? entry?.dayBen
                        : category === 'Employee'
                            ? entry?.dayEmployeeBen
                            : entry?.dayCompanyBen;

                    newRow.payBen = category === 'Total'
                        ? entry?.dayPayBen
                        : category === 'Employee'
                            ? entry?.dayEmployeePayBen
                            : entry?.dayCompanyPayBen;

                    newRow.salary = entry?.daySalary;

                    filteredResults.push(newRow);
                });
                break;
            default:
                filteredResults = null;
                break;
        }
        setFilteredDashboardInformation(filteredResults);
    };

    const updateFilters = () => {
        let filters = getValues();
        setFilterOptions({ timeSpan: filters.filterBy, year: parseInt(filters.year, 10), activeFilter: filters.view, category: filters.category });
    };

    // client-side sort - all rows for the selected year/period are already loaded, so there's no
    // need for a server round-trip the way DepositReconciliationPage.tsx's OData-backed sort needs.
    const handleSort = (field: keyof EmployeeBenefitFilteredDashboard) => {
        const descending = field === sortField ? !sortDescending : false;
        setSortField(field);
        setSortDescending(descending);

        const sorted = [...(filteredDashboardInformation ?? [])].sort((a, b) => {
            const aVal = a[field];
            const bVal = b[field];
            if (aVal === bVal) { return 0; }
            if (aVal === undefined || aVal === null) { return 1; }
            if (bVal === undefined || bVal === null) { return -1; }
            if (aVal < bVal) { return descending ? 1 : -1; }
            return descending ? -1 : 1;
        });
        setFilteredDashboardInformation(sorted);
    };

    // downloads the real Oracle-equivalent export (XL_Benefit_Dashboard) - one workbook, 4 tabs
    // (Annual/Monthly/Period/Daily), every employee for the year regardless of the Active filter
    // above, and always Company-paid dollar figures regardless of the Category filter above -
    // both match that source's own real behavior exactly (confirmed against its actual PL/SQL).
    const exportToExcel = async () => {
        const year = getValues().year;

        if (!year) {
            showToast('Please select a year first', `rgba(255,0,0,0.5)`);
            return;
        }

        await get(`/v1.0/landmen/benefits/${year}/get-benefit-dashboard-report`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `BenefitDashboard_${year}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            showToast('Unable to export report: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    // Mirrors Oracle's WHEN-MOUSE-DOUBLECLICK [oid 1868]: jumps to that employee/year on the main
    // Benefits tab (Populate_LM). Same localStorage handoff YearMatrixDialog.tsx/
    // InsuranceDashboardDialog.tsx already use.
    const drillIntoEmployee = (tsdNo: number, year: number) => {
        localStorage.setItem('personnelPendingTsdNo', tsdNo.toString());
        localStorage.setItem('personnelPendingYear', year.toString());
        props.hideDialog();
        dispatch(push('/personnel/employee-benefits'));
    };

    return (
        <Dialog title="Benefits Dashboard" dialogName={props.dialogName} bodyStyle={{ width: '85%', height: '75%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <Panel backgroundColor="#D3D3D3">
                <Panel>
                    <form>
                        <div className="form-row">
                            <RHFSelect
                                name="year"
                                label="Year"
                                labelOnLeft={true}
                                options={benefitYearsDropdown()}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                            <RHFSelect
                                name="filterBy"
                                label="Insurance By"
                                labelOnLeft={true}
                                options={[
                                    { value: 'Annual', label: 'Annual' },
                                    { value: 'Month', label: 'Month' },
                                    { value: 'Period', label: 'Pay Period' },
                                    { value: 'Day', label: 'Day' },
                                ]}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                            <RHFSelect
                                name="view"
                                label="View"
                                labelOnLeft={true}
                                options={[
                                    { value: 'Active', label: 'Active' },
                                    { value: 'Inactive', label: 'Inactive' },
                                    { value: 'All', label: 'All' },
                                ]}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                            <RHFSelect
                                name="category"
                                label="Category"
                                labelOnLeft={true}
                                options={[
                                    { value: 'Total', label: 'Total' },
                                    { value: 'Company', label: 'Company' },
                                    { value: 'Employee', label: 'Employee' },
                                ]}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                        </div>
                    </form>
                    <div style={{ overflowY: 'scroll', maxHeight: '50vh' }}>
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: '#d6d6d6' }}>
                                    {BENEFIT_COLUMNS.map(({ label, field, width }) => {
                                        const willSortDescending = field === sortField ? !sortDescending : false;
                                        const SortIcon = willSortDescending ? FaSortAlphaDown : FaSortAlphaUp;
                                        return (
                                            <th
                                                key={field}
                                                style={{ width, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                className="sticky-header"
                                                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                                                onClick={() => handleSort(field)}
                                            >
                                                {label}
                                                <SortIcon style={{ width: '0.9rem', height: '0.9rem', verticalAlign: 'middle', marginLeft: '.2rem' }} />
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            {filteredDashboardInformation?.map((row: EmployeeBenefitFilteredDashboard, index: number) => {
                                return (
                                    <tbody key={index}>
                                        <tr
                                            style={{ backgroundColor: row.defined === 'N' ? `rgb(255, 114, 118)` : undefined, cursor: 'pointer' }}
                                            title={`Double-click to open ${row.akaName}'s ${filterOptions.year} Benefits page`}
                                            onDoubleClick={() => drillIntoEmployee(row.tsdNo, filterOptions.year)}
                                        >
                                            <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {row.tsdNo}
                                            </td>
                                            <td style={{ width: '18%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {row.akaName}
                                            </td>
                                            <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.health)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.vision)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.dental)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.life)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.disability)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.ins)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.vacation)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.personal)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.holiday)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?._401k)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.fica)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.wc)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.ben)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.exPto)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.salary)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format((row?.exPto) + (row?.salary))}
                                            </td>
                                        </tr>
                                    </tbody>
                                );
                            })}
                        </table>
                    </div>
                    <div className="button-row" style={{ paddingBottom: '1rem' }}>
                        <Button
                            text="Export to Excel"
                            style={{ width: '25%' }}
                            reactIcon={FaFileExcel}
                            onClick={() => exportToExcel()}
                            intent="secondary"
                        />
                        <Button text="Close" style={{ width: '25%' }} onClick={() => props.hideDialog()} intent="primary" />
                    </div>
                </Panel>
            </Panel>
        </Dialog>
    );
};

export default BenefitDashboardDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/BenefitsDashboardDialog.tsx